import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgTooltipHeaderComponent } from 'components/AgGrid/AgTooltipHeaderComponent';
import { numberFormatter } from 'components/AgGrid/utils';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { CatalogUerBaseCostEstimateBasisRow, CollectionUerBaseCostEstimateBasisRow } from 'gql/graphql';
import type { GroupCollapseState } from 'pages/Project/UerProject/ActKC3List/components/ActKC3ListTable/types';

import type { ColSpanParams } from 'ag-grid-community';
import type { CellClassParams } from 'ag-grid-community/dist/types/core/entities/colDef';
import cn from 'classnames';
import { useMemo } from 'react';

import { CodeCellRendererComponent } from '../components/CodeCellRendererComponent';
import { CollapseHeaderButton } from '../components/CollapseHeaderButton';
import { NumberCellRendererComponent } from '../components/NumberCellRendererComponent';

import type { TUerBaseCostEstimateBasisRow } from './useUERBaseCostEstimateBasisRowsGrouped';

interface IUseCatalogUERBaseCostEstimateBasisColumns {
    changeCollapseState: (groupKey: string) => () => void;
    collapseState: GroupCollapseState;
    codeFilterData?: { label: string }[];
    nameFilterData?: { label: string }[];
    changeFilterHandler: ({ fieldName, checkedValues }: any) => void;
}

const unitCostCollapseKeys = ['unitCost.emm', 'unitCost.mtr', 'unitCost.ozp', 'unitCost.zpm'];

const totalCostCollapseKeys = [
    'totalCost.emm',
    'totalCost.mtr',
    'totalCost.ozp',
    'totalCost.zpm',
    'totalCost.nr',
    'totalCost.sp',
];

const rightAlignedFields = [
    'unitCost.total',
    'totalCost.total',
    'quantity.unit',
    'quantity.total',
    'estimateTotal',
    'unitWorkCost.tz',
    'totalWorkCost.tz',
    'unitWorkCost.tm',
    'totalWorkCost.tm',
    ...unitCostCollapseKeys,
    ...totalCostCollapseKeys,
];

const cellClass = (
    cellClassParams: CellClassParams<
        | TUerBaseCostEstimateBasisRow<CatalogUerBaseCostEstimateBasisRow>
        | TUerBaseCostEstimateBasisRow<CollectionUerBaseCostEstimateBasisRow>
    >,
) => {
    const { isSectionHeadLine, isDeleted, isNotCount } = cellClassParams.data || {};

    const isRightAlignedField = rightAlignedFields.includes(cellClassParams.colDef.field as string);

    return cn({
        'is-ordinary-row': !isSectionHeadLine,
        'is-section-headline': isSectionHeadLine,
        'is-deleted': isDeleted,
        'is-not-count': isNotCount,
        'is-right-aligned': isRightAlignedField,
    });
};

export const useUERBaseCostEstimateBasisRowsColumns = (props: IUseCatalogUERBaseCostEstimateBasisColumns) => {
    const { changeCollapseState, collapseState, changeFilterHandler, nameFilterData, codeFilterData } = props;

    return useMemo(
        () => [
            {
                field: 'number',
                headerName: '№ пп',
                cellRenderer: NumberCellRendererComponent,
                cellRendererParams: {
                    changeCollapseState,
                    collapseState,
                },
                cellClass,
                width: 144,
                colSpan: (
                    params: ColSpanParams<
                        | TUerBaseCostEstimateBasisRow<CatalogUerBaseCostEstimateBasisRow>
                        | TUerBaseCostEstimateBasisRow<CollectionUerBaseCostEstimateBasisRow>
                    >,
                ) => (params.data?.isSectionHeadLine ? 21 : 1),
            },
            {
                field: 'code',
                headerName: 'Шифр',
                cellClass,
                width: 160,
                cellRenderer: CodeCellRendererComponent,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    filterItems: codeFilterData,
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'label',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    isClientSideSearch: true,
                    childFieldAccessor: 'label',
                },
            },
            {
                field: 'name',
                headerName: 'Наименование',
                cellRendererParams: {
                    changeCollapseState,
                    collapseState,
                },
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                flex: 1,
                minWidth: 320,
                cellClass,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    filterItems: nameFilterData,
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'label',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    isClientSideSearch: true,
                    childFieldAccessor: 'label',
                },
            },
            {
                field: 'unit',
                headerName: 'Ед.изм.',
                cellClass,
                width: 120,
            },
            {
                headerName: 'Количество',
                spanHeaderHeight: true,
                width: 230,
                children: [
                    {
                        field: 'quantity.unit',
                        headerName: 'На единицу',
                        cellClass,
                        width: 125,
                        valueFormatter: numberFormatter(2),
                    },
                    {
                        field: 'quantity.total',
                        headerName: 'Всего',
                        cellClass,
                        width: 115,
                        valueFormatter: numberFormatter(2),
                    },
                ],
            },
            {
                headerName: 'Стоимость единицы в базисных ценах, руб.',
                headerGroupComponent: AgTooltipHeaderComponent,
                headerGroupComponentParams: {
                    displayName: 'Стоимость единицы в базисных ценах, руб.',
                },
                children: [
                    {
                        headerName: '',
                        field: 'unitCost.total',
                        spanHeaderHeight: true,
                        headerComponent: CollapseHeaderButton,
                        headerComponentParams: {
                            collapseKeys: unitCostCollapseKeys,
                        },
                        width: 114,
                        valueFormatter: numberFormatter(2),
                        cellClass,
                    },
                    {
                        headerName: 'ОЗП',
                        field: 'unitCost.ozp',
                        spanHeaderHeight: true,
                        width: 114,
                        valueFormatter: numberFormatter(2),
                        cellClass,
                    },
                    {
                        headerName: 'ЭММ',
                        field: 'unitCost.emm',
                        spanHeaderHeight: true,
                        width: 114,
                        valueFormatter: numberFormatter(2),
                        cellClass,
                    },
                    {
                        headerName: 'ЗПМ',
                        field: 'unitCost.zpm',
                        spanHeaderHeight: true,
                        width: 114,
                        valueFormatter: numberFormatter(2),
                        cellClass,
                    },
                    {
                        headerName: 'Материалы',
                        field: 'unitCost.mtr',
                        spanHeaderHeight: true,
                        width: 114,
                        valueFormatter: numberFormatter(2),
                        cellClass,
                    },
                ],
            },
            {
                headerName: 'Общая стоимость, руб.',
                headerGroupComponent: AgTooltipHeaderComponent,
                headerGroupComponentParams: {
                    displayName: 'Общая стоимость, руб.',
                },
                children: [
                    {
                        headerName: '',
                        field: 'totalCost.total',
                        spanHeaderHeight: true,
                        headerComponent: CollapseHeaderButton,
                        headerComponentParams: {
                            collapseKeys: totalCostCollapseKeys,
                        },
                        width: 114,
                        valueFormatter: numberFormatter(2),
                        cellClass,
                    },
                    {
                        headerName: 'ОЗП',
                        field: 'totalCost.ozp',
                        spanHeaderHeight: true,
                        width: 114,
                        valueFormatter: numberFormatter(2),
                        cellClass,
                    },
                    {
                        headerName: 'ЭММ',
                        field: 'totalCost.emm',
                        spanHeaderHeight: true,
                        width: 114,
                        valueFormatter: numberFormatter(2),
                        cellClass,
                    },
                    {
                        headerName: 'ЗПМ',
                        field: 'totalCost.zpm',
                        spanHeaderHeight: true,
                        width: 114,
                        valueFormatter: numberFormatter(2),
                        cellClass,
                    },
                    {
                        headerName: 'Материалы',
                        field: 'totalCost.mtr',
                        spanHeaderHeight: true,
                        width: 125,
                        valueFormatter: numberFormatter(2),
                        cellClass,
                    },
                    {
                        headerName: 'НР',
                        field: 'totalCost.nr',
                        spanHeaderHeight: true,
                        width: 114,
                        valueFormatter: numberFormatter(2),
                        cellClass,
                    },
                    {
                        headerName: 'СП',
                        field: 'totalCost.sp',
                        spanHeaderHeight: true,
                        width: 114,
                        valueFormatter: numberFormatter(2),
                        cellClass,
                    },
                ],
            },
            {
                headerName: 'ТЗ',
                width: 230,
                children: [
                    {
                        field: 'unitWorkCost.tz',
                        headerName: 'На единицу',
                        cellClass,
                        width: 125,
                        valueFormatter: numberFormatter(2),
                    },
                    {
                        field: 'totalWorkCost.tz',
                        headerName: 'Всего',
                        cellClass,
                        width: 115,
                        valueFormatter: numberFormatter(2),
                    },
                ],
            },
            {
                headerName: 'ТЗМ',
                width: 230,
                children: [
                    {
                        field: 'unitWorkCost.tm',
                        headerName: 'На единицу',
                        cellClass,
                        width: 125,
                        valueFormatter: numberFormatter(2),
                    },
                    {
                        field: 'totalWorkCost.tm',
                        headerName: 'Всего',
                        cellClass,
                        width: 115,
                        valueFormatter: numberFormatter(2),
                    },
                ],
            },
            {
                field: 'estimateTotal',
                headerName: 'Всего затрат',
                width: 160,
                valueFormatter: numberFormatter(2),
                cellClass,
            },
        ],
        [changeCollapseState, changeFilterHandler, codeFilterData, collapseState, nameFilterData],
    );
};
